import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { OdlBeforeCloseEvent } from '@odilo/ui-components';
import { ConnectivityService } from '../../services/connectivity/connectivity';
import { NavigateService } from '../../services/navigate/navigate';
import {
  type OfflineBlockReason,
  OfflineSessionService,
} from '../../services/offline/offline-session';

/**
 * Blocking messages of offline reading: a book that cannot be opened offline, and a connection
 * change after opening (the session keeps the mode it was opened with, as in NR1).
 */
@Component({
  selector: 'nr-offline-alert',
  imports: [TranslatePipe],
  templateUrl: './offline-alert.html',
  styleUrl: './offline-alert.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class OfflineAlertComponent {
  private static readonly BLOCK_MESSAGES: Record<OfflineBlockReason, string> = {
    notDownloaded: 'OFFLINE.NOT_DOWNLOADED',
    expired: 'OFFLINE.LOAN_EXPIRED',
    clockRolledBack: 'OFFLINE.CLOCK_ROLLED_BACK',
    offlineOnlyLink: 'OFFLINE.OFFLINE_ONLY_LINK',
  };

  private readonly offlineSession = inject(OfflineSessionService);
  private readonly navigateService = inject(NavigateService);
  private readonly connectivity = inject(ConnectivityService);

  readonly message = computed(() => {
    const reason = this.offlineSession.blockReason();
    if (reason) return OfflineAlertComponent.BLOCK_MESSAGES[reason];
    if (!this.offlineSession.connectionChanged()) return undefined;
    return this.offlineSession.isOffline()
      ? 'OFFLINE.RECOVERED_CONNECTION'
      : 'OFFLINE.LOST_CONNECTION';
  });
  readonly canGoBack = computed(
    () => this.connectivity.isOnline() && this.navigateService.hasBackUrl(),
  );

  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  preventClose(event: Event): void {
    if (event instanceof OdlBeforeCloseEvent && event.detail.source !== 'programmatic') {
      event.preventDefault();
    }
  }

  goToPlatform(): void {
    this.navigateService.goToPlatform();
  }
}
